<script setup>
/**
 * "Crop your picture" (XN-4561): the prototype's cropModal for a photo — the
 * image under a round frame, a zoom, and the picture at the size the page
 * shows it. Prototype `cropModal()` and `CROPS.photo`, build 30 Sep 12:40.
 *
 * Save uploads a 512 × 512 JPEG at quality 0.9 drawn from the crop. sso-api
 * takes PHP's default 2 MB at most and a phone's original is often more; the
 * crop is a fraction of that and still sharp at six times the 78px the page
 * draws it. A canvas starts transparent and a JPEG has none, so the canvas is
 * filled white first: a transparent PNG lands on white, not black.
 */
import { computed, nextTick, ref } from 'vue';
import { CircleStencil, Cropper, Preview } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import XlButton from '../xl/XlButton.vue';
import XlModal from '../xl/XlModal.vue';
import { uploadMyPhoto } from '../../api/portal';
import { useCropZoom, ZOOM_MAX, ZOOM_MIN } from '../../composables/useCropZoom';

const OUTPUT = { width: 512, height: 512, fillColor: '#fff' };
const QUALITY = 0.9;
const FRAME = { width: 200, height: 200 };
const SHOWN = 78;
const HOW = 'Drag to move it, zoom to fill the circle.';
const FAILED = "Couldn't save your photo — try again.";

const props = defineProps({
  // The picked file as a data: URL (not blob:, which every hub's CSP refuses to
  // the cropper's XHR); nothing to free.
  src: { type: String, required: true },
  // Takes the cropped File and resolves to the saved photo's URL.
  upload: { type: Function, default: uploadMyPhoto },
  // Smallest side, in source pixels, the picture may have; 0 takes any size.
  minimum: { type: Number, default: 0 },
});
const emit = defineEmits(['close', 'saved']);

const cropperEl = ref(null);
const { result, zoom, measure, onChange, setZoom } = useCropZoom(cropperEl);
const saving = ref(false);
const failed = ref('');
const saveButton = ref(null);
const natural = ref(null);
const tooSmall = computed(() => Boolean(natural.value) && Math.min(natural.value.width, natural.value.height) < props.minimum);
const subtitle = computed(() => (props.minimum ? `At least ${props.minimum} × ${props.minimum} · ${HOW}` : HOW));
// aria-disabled, not disabled: Save keeps the focus while it works and after a
// failure, so Tab cannot escape the modal.
const blocked = computed(() => saving.value || tooSmall.value || !result.value.image);

function onReady() {
  const image = measure()?.image;
  natural.value = image ? { width: image.width, height: image.height } : null;
}

// Nothing closes the dialog while the photo is on its way: a dialog that
// vanished mid-upload would look like a cancel that was not one.
function close() {
  if (!saving.value) emit('close');
}

const jpeg = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The crop produced no image'))), 'image/jpeg', QUALITY);
});

async function save() {
  const canvas = cropperEl.value?.getResult()?.canvas;
  if (!canvas || blocked.value) return;

  saving.value = true;
  failed.value = '';
  try {
    const blob = await jpeg(canvas);
    emit('saved', await props.upload(new File([blob], 'photo.jpg', { type: 'image/jpeg' })));
  } catch (error) {
    // A 422 carries SSO's own reason (a blank name to fix first); anything else is the generic line.
    failed.value = (error?.response?.status === 422 && error.response.data?.message) || FAILED;
  } finally {
    saving.value = false;
  }
  if (failed.value) {
    await nextTick();
    saveButton.value?.$el.focus();
  }
}
</script>

<template>
  <XlModal
    title="Crop your picture"
    :subtitle="subtitle"
    @close="close"
  >
    <!-- No debounce and no transitions (XN-4578): see composables/useCropZoom.js. -->
    <Cropper
      ref="cropperEl"
      class="pdet-crop__stage"
      background-class="pdet-crop__bg"
      :src="props.src"
      :stencil-component="CircleStencil"
      :stencil-size="FRAME"
      :stencil-props="{ aspectRatio: 1, handlers: {}, lines: {}, movable: false, resizable: false }"
      image-restriction="stencil"
      :resize-image="{ wheel: false, touch: false, adjustStencil: false }"
      :debounce="false"
      :transitions="false"
      :canvas="OUTPUT"
      @ready="onReady"
      @change="onChange"
    />

    <label class="pdet-crop__zoom">
      <span>Zoom</span>
      <input type="range" :min="ZOOM_MIN" :max="ZOOM_MAX" step="0.05" :value="zoom" @input="setZoom($event.target.value)" />
    </label>

    <div class="pdet-crop__shown">
      <div class="pdet-crop__preview">
        <Preview v-if="result.image" :width="SHOWN" :height="SHOWN" :image="result.image" :coordinates="result.coordinates" />
      </div>
      <span>Actual size — 78 × 78.</span>
    </div>

    <p v-if="tooSmall" class="pdet-crop__failed" role="alert" data-testid="photo-crop-too-small">
      This picture is {{ natural.width }} × {{ natural.height }}. Choose one at least {{ props.minimum }} × {{ props.minimum }}.
    </p>
    <p v-if="failed" class="pdet-crop__failed" role="alert">{{ failed }}</p>

    <template #actions>
      <XlButton variant="ghost" :disabled="saving" @click="close">Cancel</XlButton>
      <XlButton
        ref="saveButton"
        :aria-disabled="blocked ? 'true' : undefined"
        :aria-busy="saving ? 'true' : undefined"
        data-testid="photo-crop-save"
        @click="save"
      >{{ saving ? 'Saving…' : 'Save' }}</XlButton>
    </template>
  </XlModal>
</template>

<style scoped>
/* Measurements from the prototype's cropModal (stage 360 × 250, frame 200, preview 78). */
.pdet-crop__stage {
  width: 360px;
  max-width: 100%;
  height: 250px;
  margin: 0 auto;
  border-radius: 14px;
  overflow: hidden;
  cursor: grab;
}

/* The prototype's checkerboard, behind whatever the photo does not cover. */
.pdet-crop__stage :deep(.pdet-crop__bg),
.pdet-crop__preview {
  background-color: #fff;
  background-image:
    linear-gradient(45deg, #EDE7F0 25%, transparent 25%),
    linear-gradient(-45deg, #EDE7F0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #EDE7F0 75%),
    linear-gradient(-45deg, transparent 75%, #EDE7F0 75%);
  background-size: 16px 16px;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0;
}

/* The frame: a white ring, and the surround darkened as the prototype darkens it. */
.pdet-crop__stage :deep(.vue-advanced-cropper__foreground) { background: #1C1720; opacity: 0.55; }
.pdet-crop__stage :deep(.vue-circle-stencil__preview) { box-shadow: 0 0 0 2px #fff; }

.pdet-crop__zoom { display: flex; align-items: center; gap: 12px; margin-top: 16px; }
.pdet-crop__zoom span { flex: 0 0 auto; font-size: 12.5px; color: var(--xl-color-outline); }
.pdet-crop__zoom input { flex: 1; min-width: 0; accent-color: var(--xl-color-primary); }

.pdet-crop__shown {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid var(--xl-color-border-subtle);
}
.pdet-crop__preview {
  box-sizing: content-box;
  width: 78px;
  height: 78px;
  flex: 0 0 78px;
  overflow: hidden;
  border: 1px solid var(--xl-color-border);
  border-radius: 50%;
}
.pdet-crop__shown span { font-size: 12.5px; line-height: 1.5; color: var(--xl-color-outline); }

:deep(.xl-btn[aria-disabled="true"]) { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }

.pdet-crop__failed { margin: 14px 0 0; font-size: 13.5px; color: var(--xl-color-error); }
</style>
